const agentShader = /* wgsl */ `
struct Agent {
    position: vec2f,
    angle: f32,
    strain: u32,
};

struct Simulation {
    deltaTime: f32,
    agentCount: u32,
    trailWidth: u32,
    trailHeight: u32,

    speeds: vec4f,
    sensorDistances: vec4f,
    sensorAngles: vec4f,
    turnSpeeds: vec4f,

    interaction0: vec4f,
    interaction1: vec4f,
    interaction2: vec4f,
    interaction3: vec4f,

    topology: vec4u,
};

struct PointerInteraction {
    position: vec2f,
    radius: f32,
    strength: f32,
};

@group(0) @binding(0)
var<storage, read_write> agents: array<Agent>;

@group(0) @binding(1)
var<uniform> simulation: Simulation;

@group(0) @binding(2)
var<storage, read_write> deposits: array<atomic<u32>>;

@group(0) @binding(3)
var trail: texture_2d<f32>;

@group(0) @binding(4)
var boundaries: texture_2d<f32>;

@group(0) @binding(5)
var<uniform> pointer: PointerInteraction;

fn getChannel(value: vec4f, channel: u32) -> f32 {
    switch channel {
        case 0u: {
            return value.r;
        }
        case 1u: {
            return value.g;
        }
        case 2u: {
            return value.b;
        }
        default: {
            return value.a;
        }
    }
}

fn getInteractionWeights(strain: u32) -> vec4f {
    switch strain {
        case 0u: {
            return simulation.interaction0;
        }
        case 1u: {
            return simulation.interaction1;
        }
        case 2u: {
            return simulation.interaction2;
        }
        default: {
            return simulation.interaction3;
        }
    }
}

fn wrapsBounds() -> bool {
    return simulation.topology.x != 0u;
}

fn isOutside(position: vec2f) -> bool {
    return (
        position.x < 0.0 ||
        position.x >= 1.0 ||
        position.y < 0.0 ||
        position.y >= 1.0
    );
}

fn positionToPixel(position: vec2f) -> vec2i {
    let safePosition = clamp(
        position,
        vec2f(0.0),
        vec2f(0.999999)
    );

    let pixelX = min(
        u32(safePosition.x * f32(simulation.trailWidth)),
        simulation.trailWidth - 1u
    );

    let pixelY = min(
        u32(safePosition.y * f32(simulation.trailHeight)),
        simulation.trailHeight - 1u
    );

    return vec2i(i32(pixelX), i32(pixelY));
}

fn isBlocked(position: vec2f) -> bool {
    if (!wrapsBounds() && isOutside(position)) {
        return true;
    }

    let pixel = positionToPixel(position);
    let boundary = textureLoad(boundaries, pixel, 0).r;

    return boundary > 0.5;
}

fn escapeBoundary(position: vec2f) -> vec2f {
    if (!isBlocked(position)) {
        return position;
    }

    let center = positionToPixel(position);
    let width = i32(simulation.trailWidth);
    let height = i32(simulation.trailHeight);

    for (var radius = 1; radius <= 12; radius++) {
        for (var y = -radius; y <= radius; y++) {
            for (var x = -radius; x <= radius; x++) {
                if (abs(x) != radius && abs(y) != radius) {
                    continue;
                }

                let px = center.x + x;
                let py = center.y + y;

                if (
                    px < 0 ||
                    px >= width ||
                    py < 0 ||
                    py >= height
                ) {
                    continue;
                }

                let candidate = vec2f(
                    (f32(px) + 0.5) / f32(simulation.trailWidth),
                    (f32(py) + 0.5) / f32(simulation.trailHeight)
                );

                if (!isBlocked(candidate)) {
                    return candidate;
                }
            }
        }
    }

    return position;
}

fn sense(
    agent: Agent,
    angleOffset: f32,
    sensorDistance: f32
) -> f32 {
    let angle = agent.angle + angleOffset;
    let direction = vec2f(cos(angle), sin(angle));

    let pixelSize = vec2f(
        1.0 / f32(simulation.trailWidth),
        1.0 / f32(simulation.trailHeight)
    );

    var sensorPosition =
        agent.position +
        direction * sensorDistance * pixelSize;

    if (wrapsBounds()) {
        sensorPosition = fract(sensorPosition + vec2f(1.0));
    } else if (isOutside(sensorPosition)) {
        return -1000.0;
    }

    if (isBlocked(sensorPosition)) {
        return -1000.0;
    }

    let pixel = positionToPixel(sensorPosition);
    let trailValue = textureLoad(trail, pixel, 0);
    let weights = getInteractionWeights(agent.strain);

    return dot(trailValue, weights);
}

@compute
@workgroup_size(64)
fn computeMain(
    @builtin(global_invocation_id) id: vec3u,
    @builtin(num_workgroups) numWorkgroups: vec3u
) {
    // Reconstruct the linear agent index from the 2D dispatch.
    let rowWidth = numWorkgroups.x * 64u;
    let index = id.x + id.y * rowWidth;

    if (index >= simulation.agentCount) {
        return;
    }

    var agent = agents[index];

    // Move agents out if a wall was painted directly over them.
    if (isBlocked(agent.position)) {
        let escapedPosition = escapeBoundary(agent.position);

        if (
            escapedPosition.x != agent.position.x ||
            escapedPosition.y != agent.position.y
        ) {
            agent.position = escapedPosition;

            agent.angle +=
                sin(f32(index) * 12.9898) *
                3.14159265;
        }
    }

    let strain = min(agent.strain, 3u);

    let speed = getChannel(
        simulation.speeds,
        strain
    );

    let sensorDistance = getChannel(
        simulation.sensorDistances,
        strain
    );

    let sensorAngle = getChannel(
        simulation.sensorAngles,
        strain
    );

    let turnSpeed = getChannel(
        simulation.turnSpeeds,
        strain
    );

    let forward = sense(
        agent,
        0.0,
        sensorDistance
    );

    let left = sense(
        agent,
        sensorAngle,
        sensorDistance
    );

    let right = sense(
        agent,
        -sensorAngle,
        sensorDistance
    );

    if (
        forward >= left &&
        forward >= right
    ) {
        // Keep heading.
    } else if (left > right) {
        agent.angle +=
            turnSpeed *
            simulation.deltaTime;
    } else if (right > left) {
        agent.angle -=
            turnSpeed *
            simulation.deltaTime;
    }

    // Positive pointer strength attracts; negative strength repels.
    if (
        pointer.radius > 0.0 &&
        pointer.strength != 0.0
    ) {
        var toPointer =
            pointer.position -
            agent.position;

        // Use the shortest path across the toroidal domain.
        if (wrapsBounds()) {
            toPointer -= round(toPointer);
        }

        let distance = length(toPointer);

        if (
            distance < pointer.radius &&
            distance > 0.00001
        ) {
            let influence =
                1.0 -
                distance / pointer.radius;

            var targetDirection = normalize(toPointer);

            if (pointer.strength < 0.0) {
                targetDirection = -targetDirection;
            }

            let targetAngle = atan2(
                targetDirection.y,
                targetDirection.x
            );

            var angleDifference =
                targetAngle -
                agent.angle;

            angleDifference = atan2(
                sin(angleDifference),
                cos(angleDifference)
            );

            agent.angle +=
                angleDifference *
                influence *
                abs(pointer.strength) *
                simulation.deltaTime;
        }
    }

    let direction = vec2f(
        cos(agent.angle),
        sin(agent.angle)
    );

    var proposedPosition =
        agent.position +
        direction *
        speed *
        simulation.deltaTime;

    if (wrapsBounds()) {
        proposedPosition = fract(
            proposedPosition +
            vec2f(1.0)
        );
    } else {
        if (proposedPosition.x < 0.0) {
            proposedPosition.x = -proposedPosition.x;
            agent.angle = 3.14159265 - agent.angle;
        } else if (proposedPosition.x >= 1.0) {
            proposedPosition.x = 2.0 - proposedPosition.x;
            agent.angle = 3.14159265 - agent.angle;
        }

        if (proposedPosition.y < 0.0) {
            proposedPosition.y = -proposedPosition.y;
            agent.angle = -agent.angle;
        } else if (proposedPosition.y >= 1.0) {
            proposedPosition.y = 2.0 - proposedPosition.y;
            agent.angle = -agent.angle;
        }

        proposedPosition = clamp(
            proposedPosition,
            vec2f(0.000001),
            vec2f(0.999999)
        );
    }

    if (isBlocked(proposedPosition)) {
        agent.angle +=
            3.14159265 +
            sin(f32(index) * 12.9898) *
            0.65;
    } else {
        agent.position = proposedPosition;
    }

    let pixel = positionToPixel(agent.position);

    let pixelIndex =
        u32(pixel.y) *
        simulation.trailWidth +
        u32(pixel.x);

    let depositIndex =
        pixelIndex *
        4u +
        strain;

    atomicAdd(
        &deposits[depositIndex],
        1u
    );

    agents[index] = agent;
}
`;

export default agentShader;
